import type { UserProfile } from './user-profile.model';

/** Shell-owned authentication status values shared across micro frontends. */
export enum AuthSessionStatus {
  /** No active authenticated session exists. */
  SignedOut = 'signed-out',
  /** Interactive sign-in is in progress. */
  SigningIn = 'signing-in',
  /** User is authenticated and session is active. */
  SignedIn = 'signed-in',
  /** User is idle and currently inside warning/grace window. */
  IdleWarning = 'idle-warning',
  /** Sign-out flow is in progress. */
  SigningOut = 'signing-out',
  /** Session is no longer valid and requires re-authentication. */
  Expired = 'expired',
}

/** Shell-owned session state shared across micro frontends. All date fields are ISO-8601 strings. */
export interface AuthenticationSession {
  /** Current authentication state for the running browser tab. */
  status: AuthSessionStatus;
  /** Subject identifier from the identity token (sub claim). */
  subjectId: string;
  /** When the session was issued. */
  issuedAt: string;
  /** When the session token expires. expiresAt must be later than issuedAt. */
  expiresAt: string;
  /** Timestamp of the last recorded user activity. */
  lastActivityAt: string;
  /** Set when status is idle-warning; null otherwise. */
  idleWarningAt: string | null;
  /** Grace period end time; set when status is idle-warning; null otherwise. */
  graceEndsAt: string | null;
  /** Permissions parsed from entitlement claim in customAccessToken. */
  permissions?: string[];
  /** Custom Access Token parsed from idToken. */
  customAccessToken?: string;
  /** Raw ID token from B2C. Used for FHIR API calls that require the B2C token directly. */
  idToken?: string;
  /** User profile information extracted from customAccessToken claims. */
  userProfile?: UserProfile;
}

/** Session inactivity handling configuration. */
export interface IdlePolicy {
  /** Seconds of inactivity before the idle-warning state is entered. */
  idleThresholdSeconds: number;
  /** Duration (seconds) the idle warning is shown before the grace period begins. */
  warningDurationSeconds: number;
  /** Seconds after warning before the policy action fires. Must be >= warningDurationSeconds. */
  gracePeriodSeconds: number;
  /** Action to perform when grace period expires. */
  onGraceExpired: 'global-signout';
}
